import "./index.css";
import { StrictMode, useEffect, useState, useRef } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Navigate, Route, Routes, useNavigate } from "react-router-dom";
import Dashboard from "./pages/Dashboard";
import Sidebar from "./components/Sidebar";
import LeadsPage from "./pages/LeadsPage";
import DonorsPage from "./pages/DonorsPage";
import OwnerDetails from "./pages/details/OwnerDetails";
import ContactDetails from "./pages/details/ContactDetails";
import AccountDetails from "./pages/details/AccountDetails";
import AccountsPage from "./pages/AccountsPage";
import TransactionsPage from "./pages/TransactionsPage";
import TransactionDetails from "./pages/details/TransactionDetails";
import CallsPage from "./pages/CallsPage";
import CallDetails from "./pages/details/CallDetails.jsx";
import PaymentsPage from "./pages/PaymentsPage.jsx";
import PaymentDetails from "./pages/details/PaymentDetails.jsx";
import LeadsFormPage from "./pages/create/LeadsFormPage.jsx";
import LeadsEdit from "./pages/edit/LeadEdit.jsx";
import TasksPage from "./pages/TasksPage.jsx";
import TaskDetails from "./pages/details/TaskDetails.jsx";
import TasksForm from "./pages/create/TasksFormPage.jsx";
import TaskEdit from "./pages/edit/TaskEdit.jsx";
import CartPage from "./pages/CartPage.jsx";
import ScriptPage from "./pages/ScriptPage.jsx";
import ScriptDetails from "./pages/details/ScriptDetails.jsx";
import ContactEdit from './pages/edit/ContactEdit.jsx';
import { UserContextProvider } from "./context/DealerProfile.jsx";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useTasks } from "./components/fetcher.jsx";
import StickyBell from "./components/StickyBell.jsx";
import { X } from "lucide-react";
import TestPage from './pages/Test.jsx';
import { AllStateProviders } from "./context/globalState.jsx";
import { useUserContext } from "./context/DealerProfile.jsx";

// Helper for avatar initials (fallback)
function getInitials(name, last) {
  if (!name && !last) return "👤";
  return ((name ? name[0] : "") + (last ? last[0] : "")).toUpperCase();
}

function AppContent() {
  const { dealerDetail } = useUserContext();
  const { tasks, birthdays, loading, error } = useTasks();
  const notifiedTasks = useRef(new Set());
  const notifiedBirthdays = useRef(new Set());
  const navigate = useNavigate();

  const [birthdayModalPeople, setBirthdayModalPeople] = useState([]);
  const [clickedIds, setClickedIds] = useState(() => {
    const stored = localStorage.getItem("clickedBirthdays");
    return stored ? JSON.parse(stored) : [];
  });

  const handlePersonClick = (id) => {
    setClickedIds((prev) => {
      if (prev.includes(id)) return prev;
      const updated = [...prev, id];
      localStorage.setItem("clickedBirthdays", JSON.stringify(updated));
      return updated;
    });
  };

  // Filter birthdays that are not clicked yet
  const visibleBirthdays = birthdays.filter(
    (person) => !clickedIds.includes(person.id)
  );

  // ---- Filter only today's birthdays ----
  const today = new Date();
  const todaysBirthdays = visibleBirthdays.filter((person) => {
    const dob = new Date(person.Date_of_Birth);
    return (
      dob.getDate() === today.getDate() &&
      dob.getMonth() === today.getMonth()
    );
  });

  function BirthdayModal({ people, onClose, onPersonClick }) {
    if (!people || people.length === 0) return null;
    return (
      <div
        className="fixed inset-0 bg-black/30 flex items-center justify-center"
        style={{ zIndex: 99999 }}
      >
        <div className="relative bg-white px-7 py-8 rounded-lg shadow-xl max-w-md w-full text-center border-t-4 border-[#10385B] max-h-[90vh] overflow-hidden">
          {/* Close button */}
          <button
            onClick={onClose}
            aria-label="Close"
            className="absolute top-3 right-3 text-[#10385B] hover:bg-[#10385B] hover:text-white rounded-full p-2 transition"
          >
            <X size={22} />
          </button>

          {/* Header */}
          <h2 className="text-2xl font-bold mb-6 text-[#10385B] flex items-center justify-center gap-2">
            <span>🎉</span> Birthday Alert!
          </h2>

          <div className="space-y-4 overflow-y-auto max-h-[60vh] pr-2">
            {people.map((person) => (
              <div
                key={person.id}
                onClick={() => {
                  const path =
                    person.source === "lead"
                      ? `/app/owner-details/${person.id}`
                      : `/app/contact-details/${person.id}`;
                  window.open(path, "_blank");
                  onPersonClick?.(person.id);
                }}
                className="flex items-center space-x-4 px-4 py-3 bg-[#10385B]/10 rounded cursor-pointer hover:bg-[#10385B]/20 transition"
              >
                {person.photoUrl ? (
                  <img
                    src={person.photoUrl}
                    alt={`${person.First_Name}'s avatar`}
                    className="w-12 h-12 rounded-full border-2 border-[#10385B] object-cover bg-white"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-[#10385B] flex items-center justify-center font-bold text-lg text-white ring-2 ring-white">
                    {getInitials(person.First_Name, person.Last_Name)}
                  </div>
                )}
                <div className="flex-1 text-left">
                  <p className="text-lg font-semibold text-[#10385B]">
                    It’s{" "}
                    <span className="underline">{`${person.First_Name || ""} ${person.Last_Name || ""
                      }`.trim()}</span>
                    ’s birthday today!
                  </p>
                  <p className="text-sm text-[#10385B] opacity-80">
                    Don’t forget to wish them <span className="text-lg">🎂</span>
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  useEffect(() => {
    if (loading || error) return;

     // Only proceed if the user is active
    if (dealerDetail?.User_Status === "Inactive") return;

    const now = new Date();
    const in15Minutes = new Date(now.getTime() + 15 * 60 * 1000);

    // Task Notifications
    tasks?.forEach((task) => {
      // console.log(task.Due_Date_Time);
      if (task.Due_Date_Time && !notifiedTasks.current.has(task.id)) {
        const dueDateTime = new Date(task.Due_Date_Time);
        if (dueDateTime > now && dueDateTime <= in15Minutes) {
          // console.log("HiS");
          toast.info(`⏰ Task "${task.Subject}" is due soon!`, {
            toastId: `task-${task.id}`,
          });
          notifiedTasks.current.add(task.id);
        }
      }
    });

    // Birthday Notifications — ONLY Today's
    const unseen = todaysBirthdays.filter(
      (p) => !notifiedBirthdays.current.has(p.id)
    );
    if (unseen.length > 0) {
      setBirthdayModalPeople(unseen);
      unseen.forEach((p) => notifiedBirthdays.current.add(p.id));
    }
  }, [tasks, todaysBirthdays, loading, error]);

  return (
    <>
      <ToastContainer
        position="bottom-right"
        autoClose={8000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
        draggable 
      />

      {/* Show only today's birthdays */}
      {birthdayModalPeople.length > 0 && (
        <BirthdayModal
          people={birthdayModalPeople}
          onPersonClick={handlePersonClick}
          onClose={() => setBirthdayModalPeople([])}
        />
      )}

      <div className="h-full w-full flex overflow-hidden overflow-x-hidden">
        <Sidebar />
        <div className="flex-1 h-screen overflow-x-hidden pl-4 box-border rounded-tl-[0px] bg-[#F1F5F9]">
          <Routes>
            <Route path="/" element={<Navigate to="/home" />} />
            <Route path="/index.html" element={<Navigate to="/home" />} />
            <Route path="/home" element={<Dashboard />} />
            <Route path="/potential-donor" element={<LeadsPage />} />
            <Route path="/existing-donor" element={<DonorsPage />} />
            <Route path="/manual-calls" element={<CallsPage />} />
            <Route path="/accounts" element={<AccountsPage />} />
            <Route path="/owner-details/:id" element={<OwnerDetails />} />
            <Route path="/contact-details/:id" element={<ContactDetails />} />
            <Route path="/account-details/:id" element={<AccountDetails />} />
            <Route path="/call-details/:id" element={<CallDetails />} />
            <Route path="/transactions" element={<TransactionsPage />} />
            <Route path="/transaction-details/:id" element={<TransactionDetails />} />
            <Route path="/payments" element={<PaymentsPage />} />
            <Route path="/payment-details/:id" element={<PaymentDetails />} />
            <Route path="/create-lead" element={<LeadsFormPage />} />
            <Route path="/edit-lead/:id" element={<LeadsEdit />} />
            <Route path="/edit-contact-details/:id" element={<ContactEdit />} />
            <Route path="/tasks" element={<TasksPage />} />
            <Route path="/task-edit/:id" element={<TaskEdit />} />
            <Route path="/create-task" element={<TasksForm />} />
            <Route path="/task-details/:id" element={<TaskDetails />} />
            <Route path="/script" element={<ScriptPage />} />
           <Route path="/test" element={<TestPage />} />
           <Route path="/view-cart" element={<CartPage />} />
            <Route path="/script-details/:id" element={<ScriptDetails />} />
          </Routes>
        </div>
        {dealerDetail?.User_Status !== "Inactive" && (
          <StickyBell tasks={tasks || []} />
        )}

      </div>
    </>
  );
}

function App() {
  return (
    <UserContextProvider>
      <StrictMode>
        <AllStateProviders>
          <BrowserRouter basename="/app">
            <AppContent />
          </BrowserRouter>
        </AllStateProviders>
      </StrictMode>
    </UserContextProvider>
  );
}

createRoot(document.getElementById("root")).render(<App />);
